import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SecretContract } from '../../contracts/secret';
import ConfirmationModal from './ConfirmationModal';
import { MESSAGES } from '../../constants/messages';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

interface SecretListProps {
    secrets: SecretContract[];
    onDeleteSecret: (secretId: string) => void;
}

const SecretList: React.FC<SecretListProps> = ({ secrets, onDeleteSecret }) => {
    const { t, i18n } = useTranslation();
    const [copiedSecretId, setCopiedSecretId] = useState<string | null>(null);
    const [visibleSecrets, setVisibleSecrets] = useState<Set<string>>(() => {
        const initialVisible = new Set<string>();
        secrets.forEach((secret) => {
            if (secret.secretText && secret.secretText.length > 10) {
                initialVisible.add(secret.id);
            }
        });
        return initialVisible;
    });
    const [deleteSecretId, setDeleteSecretId] = useState<string | null>(null);
    const [isDeletingSecret, setIsDeletingSecret] = useState(false);

    useEffect(() => {
        const newVisible = new Set(visibleSecrets);
        let updated = false;
        
        secrets.forEach((secret) => {
            if (secret.secretText && secret.secretText.length > 10 && !visibleSecrets.has(secret.id)) {
                newVisible.add(secret.id);
                updated = true;
            }
        });
        
        if (updated) {
            setVisibleSecrets(newVisible);
        }
    }, [secrets]);

    const handleCopy = async (text: string, secretId: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedSecretId(secretId);
            setTimeout(() => setCopiedSecretId(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const toggleSecretVisibility = (secretId: string) => {
        const newVisible = new Set(visibleSecrets);
        if (newVisible.has(secretId)) {
            newVisible.delete(secretId);
        } else {
            newVisible.add(secretId);
        }
        setVisibleSecrets(newVisible);
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString(i18n.language, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const isExpired = (endAt: string) => {
        return new Date(endAt) < new Date();
    };

    if (secrets.length === 0) {
        return (
            <div className="secrets-empty">
                <p>{t('modals.noApiKeys')}</p>
            </div>
        );
    }

    return (
        <div className="secrets-list">
            {secrets.map((secret) => (
                <div
                    key={secret.id}
                    className={`secret-item ${isExpired(secret.endAt) ? 'expired' : ''}`}
                >
                    <div className="secret-header">
                        <div className="secret-date-container">
                            {new Date(secret.endAt).toLocaleDateString(i18n.language)}
                            {isExpired(secret.endAt) && (
                                <span className="secret-expired-badge">{t('subscriptions.expired')}</span>
                            )}
                        </div>
                        <button
                            className="secret-delete-btn"
                            onClick={() => setDeleteSecretId(secret.id)}
                            title={t('modals.deleteSecretTooltip')}
                        >
                            <DeleteIcon fontSize="small" />
                        </button>
                    </div>

                    <div className="secret-field">
                        <label className="secret-label">{t('modals.secret')}</label>
                        <div className="secret-value-container">
                            <input
                                type={visibleSecrets.has(secret.id) ? 'text' : 'password'}
                                value={
                                    visibleSecrets.has(secret.id)
                                        ? secret.secretText || `••••${secret.hint}`
                                        : `••••${secret.hint}`
                                }
                                readOnly
                                className="secret-value"
                            />
                            <button
                                className="secret-action-btn"
                                onClick={() => toggleSecretVisibility(secret.id)}
                                title={
                                    visibleSecrets.has(secret.id) ? 'Hide secret' : 'Show secret'
                                }
                            >
                                {visibleSecrets.has(secret.id) ? (
                                    <VisibilityOffIcon fontSize="small" />
                                ) : (
                                    <VisibilityIcon fontSize="small" />
                                )}
                            </button>
                            <button
                                className={`secret-action-btn ${
                                    copiedSecretId === secret.id ? 'copied' : ''
                                }`}
                                onClick={() =>
                                    handleCopy(secret.secretText || secret.hint, secret.id)
                                }
                                title={t('modals.copyToClipboardTooltip')}
                            >
                                <ContentCopyIcon fontSize="small" />
                            </button>
                            {copiedSecretId === secret.id && (
                                <span className="copy-success-message">{t('applications.copied')}</span>
                            )}
                        </div>
                    </div>

                    <div className="secret-dates">
                        <div className="secret-date-field">
                            <span className="secret-date-label">{t('modals.created')}</span>
                            <span className="secret-date-value">{formatDate(secret.startAt)}</span>
                        </div>
                        <div className="secret-date-field">
                            <span className="secret-date-label">{t('modals.expires')}</span>
                            <span className="secret-date-value">{formatDate(secret.endAt)}</span>
                        </div>
                    </div>
                </div>
            ))}

            <ConfirmationModal
                isOpen={deleteSecretId !== null}
                title={t('modals.deleteSecret')}
                message={t('modals.deleteSecretConfirm')}
                onConfirm={async () => {
                    if (deleteSecretId) {
                        setIsDeletingSecret(true);
                        try {
                            await onDeleteSecret(deleteSecretId);
                        } finally {
                            setIsDeletingSecret(false);
                            setDeleteSecretId(null);
                        }
                    }
                }}
                onCancel={() => setDeleteSecretId(null)}
                confirmText={t('common.yes')}
                cancelText={t('common.no')}
                isLoading={isDeletingSecret}
            />
        </div>
    );
};

export default SecretList;
